// ── Global "Tell Me" search palette ────────────────────────────────────────
// The BC Alt+Q equivalent. Mounted once in FinanceShell so it is available on
// every route. Open it with Ctrl/⌘+K (or "/" outside a text field), via the
// floating trigger, or by dispatching the window event 'edumis:open-search'
// (used by the Dashboard toolbar button). Results are permission-filtered per
// user — see config/searchIndex.js.
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog, DialogContent, InputBase, Box, List, ListItemButton,
  ListItemText, Typography, Chip, Fab, Tooltip, Divider,
} from '@mui/material';
import { Search as SearchIcon, KeyboardReturn, TravelExplore } from '@mui/icons-material';
import { buildSearchIndex, searchEntries } from '../config/searchIndex';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const OPEN_EVENT = 'edumis:open-search';

export default function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  // Re-read the session on every navigation so the palette appears right after
  // login (SPA navigate, no reload) and hides after sign-out.
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || 'null'),
    [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  // Rebuild the index each time the palette opens so mid-session permission
  // changes (an admin granting a module) are reflected without a reload.
  const index = useMemo(() => (open ? buildSearchIndex(user) : []), [open, user]);
  const results = useMemo(() => searchEntries(index, query), [index, query]);

  const openPalette = useCallback(() => { setQuery(''); setActive(0); setOpen(true); }, []);
  const close = useCallback(() => setOpen(false), []);

  // Global shortcuts + external open event.
  useEffect(() => {
    const onKey = (e) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); openPalette(); return; }
      // "/" opens search unless the user is typing into a field.
      if (e.key === '/' && !isTypingTarget(e.target)) { e.preventDefault(); openPalette(); }
    };
    const onOpenEvent = () => openPalette();
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpenEvent);
    };
  }, [openPalette]);

  useEffect(() => { setActive(0); }, [query]);

  // Parents use the portal navigation, not this staff palette.
  if (!user || user.role === 'parent') return null;

  const choose = (entry) => {
    if (!entry) return;
    close();
    navigate(entry.path);
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  return (
    <>
      {/* Always-present floating trigger (discoverable on every page). */}
      <Tooltip title={`Search pages, lists & reports  (${isMac ? '⌘' : 'Ctrl'}+K)`} placement="left">
        <Fab size="medium" color="primary" onClick={openPalette}
          aria-label="Search"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.drawer - 1 }}>
          <SearchIcon />
        </Fab>
      </Tooltip>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, position: 'fixed', top: 80, m: 0, alignSelf: 'flex-start' } }}
        TransitionProps={{ onEntered: () => inputRef.current?.focus() }}>
        {/* Search box */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
          <SearchIcon color="action" />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Tell me what you want to do…"
            fullWidth
            sx={{ fontSize: 18 }}
          />
          <Chip size="small" variant="outlined" label="Esc" onClick={close} sx={{ cursor: 'pointer' }} />
        </Box>
        <Divider />

        <DialogContent sx={{ p: 0, maxHeight: '55vh' }}>
          {query.trim() === '' ? (
            <Box sx={{ px: 3, py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <TravelExplore sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2">
                Search any page, list or report you have access to.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                Try “merit list”, “aged receivables”, “payroll runs”, “users”.
              </Typography>
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ px: 3, py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No results for “{query}”.</Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                Nothing matched, or you don’t have access to it.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {results.map((r, i) => (
                <ListItemButton
                  key={r.path}
                  selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(r)}
                  sx={{ px: 2, py: 1, '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' } } }}>
                  <SearchIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.6 }} />
                  <ListItemText
                    primary={r.label}
                    secondary={`${r.moduleTitle} › ${r.group}`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14.5 }}
                    secondaryTypographyProps={{ fontSize: 11.5, sx: { color: i === active ? 'rgba(255,255,255,0.8)' : 'text.secondary' } }}
                  />
                  {i === active && <KeyboardReturn fontSize="small" sx={{ opacity: 0.8 }} />}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>

        {/* Footer hint */}
        <Divider />
        <Box sx={{ px: 2, py: 0.75, display: 'flex', gap: 2, color: 'text.secondary', flexWrap: 'wrap' }}>
          <Hint keys="↑ ↓" label="navigate" />
          <Hint keys="↵" label="open" />
          <Hint keys={isMac ? '⌘K' : 'Ctrl K'} label="search anywhere" />
        </Box>
      </Dialog>
    </>
  );
}

const Hint = ({ keys, label }) => (
  <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Box component="span" sx={{ px: 0.75, py: 0.15, borderRadius: 1, bgcolor: 'action.hover', fontWeight: 700, fontSize: 11 }}>{keys}</Box>
    {label}
  </Typography>
);

// True when focus is in an input the "/" shortcut should not hijack.
function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

// Helper for other components to open the palette without importing this module.
export const openGlobalSearch = () => window.dispatchEvent(new Event(OPEN_EVENT));
